<script lang="ts">
export default {
  name: 'Step4ClientDemographics',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, watchEffect, onMounted, onBeforeUnmount, inject } from 'vue';
import { accountService, referenceService } from '@/services/api';
import { maxLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { AxiosError } from 'axios';
import { Check, CircleCheck, TriangleAlert } from '@lucide/vue';
import { FailedToSaveAccount, InvalidDomainName, RemoveWWW, TooLong } from '@/utilities/Errors';
import { AppState } from '@/store';
import { Store } from 'vuex';
import { IsUserAnAdmin } from '@/utilities/AuthUtils';
import { orgName } from '@/utilities/OrgConfig';
import { Account, PublicCompanySearchResult } from '@/types/Account';
import { ClientIndustry, Industry } from '@/types/Industry';
import { StockExchange } from '@/types/StockExchange';
import { AccountSummary } from '@/types/AccountSummary';
import Alert from '@/components/v2/Alert.vue';
import AppModal from '@/components/v2/AppModal.vue';
import SICCodesAutocompleteInput from '@/components/SICCodesAutocompleteInput.vue';
import TextField from '@/components/v2/TextField.vue';
import Combobox from '@/components/v2/Combobox.vue';
import Spinner from '@/components/v2/Spinner.vue';
import { Info } from '@lucide/vue';
import { useToast } from '@/composables/useToast';
import { useDataReadinessWizard } from '@/composables/useDataReadinessWizard';
import { validDomain, noWWW, isDomainFormatInvalid } from '@/utilities/dataReadinessIssues';

interface Form {
  capIQId: number | null;
  sic: string;
  stockTicker: string;
  stockExchange: string | null;
  ownershipType: number | null;
  parentAccountID: number | null;
  domainName: string;
  domainDoesNotExist: boolean;
  noParentAccount: boolean;
  accountAttributes: number[];
  industry: number | null;
  displayName: string;
}

const store = inject<Store<AppState>>('store');
const toast = useToast();
const wizard = useDataReadinessWizard();

// -----
// data
// -----
const loading = ref<boolean>(true);
const account = ref<Account>({} as Account);
const allSics = ref<ClientIndustry[]>([]);
const allIndustries = ref<Industry[]>([]);
const stockExchanges = ref<StockExchange[]>([]);
const allCompanies = ref<AccountSummary[]>([]);
const accountAttributes = ref<Record<string, string>[]>([]);
const showCompanyPickerModal = ref<boolean>(false);
const companySearchLoading = ref<boolean>(false);
const companySearchResults = ref<PublicCompanySearchResult[]>([]);
const companySearchSelection = ref<number | undefined>(undefined);
const initialFormValue = ref<Form>();

const form = ref<Form>({
  capIQId: null,
  sic: '',
  stockTicker: '',
  stockExchange: null,
  ownershipType: null,
  parentAccountID: null,
  domainName: '',
  domainDoesNotExist: false,
  noParentAccount: false,
  accountAttributes: [],
  industry: null,
  displayName: '',
});

// ------------
// validation
// ------------
const validateRules = {
  form: {
    stockTicker: {
      maxLength: maxLength(10),
    },
    domainName: {
      maxLength: maxLength(100),
      validDomain,
      noWWW,
    },
    displayName: {
      maxLength: maxLength(100),
    },
  },
};

const v$ = useVuelidate<Form>(validateRules, form);

// ---------
// computed
// ---------
const isAdmin = computed(() => IsUserAnAdmin(store?.state.user));

const stockTickerErrors = computed(() => {
  const errors: string[] = [];
  if (!v$.value.form.stockTicker.$dirty) return errors;
  if (!v$.value.form.stockTicker.maxLength) errors.push(TooLong(10));
  return errors;
});

const domainNameErrors = computed(() => {
  const errors: string[] = [];
  if (!v$.value.form.domainName.$dirty) return errors;
  if (!v$.value.form.domainName.maxLength) errors.push(TooLong(100));
  if (!v$.value.form.domainName.noWWW) errors.push(RemoveWWW);
  if (!v$.value.form.domainName.validDomain) errors.push(InvalidDomainName);
  return errors;
});

const displayNameErrors = computed(() => {
  const errors: string[] = [];
  if (!v$.value.form.displayName.$dirty) return errors;
  if (!v$.value.form.displayName.maxLength) errors.push(TooLong(100));
  return errors;
});

// Domain validity is independent of vuelidate's $dirty tracking so the Next
// button stays correctly gated even before the field is touched.
const domainInvalid = computed(() => {
  if (form.value.domainDoesNotExist) return false;
  const d = form.value.domainName;
  if (!d) return false;
  return isDomainFormatInvalid(d);
});

const companies = computed(() =>
  allCompanies.value.filter((company) => company.accountID !== Number(account.value.accountID) && !!company.accountID),
);

const industryOptions = computed(() => allIndustries.value.map((i) => ({ label: i.name, value: String(i.ID) })));
const industryModel = computed({
  get: () => (form.value.industry != null ? String(form.value.industry) : ''),
  set: (v) => {
    form.value.industry = v === '' ? null : Number(v);
  },
});

const stockExchangeOptions = computed(() => stockExchanges.value.map((e) => ({ label: `${e.Abbrev} - ${e.Name}`, value: e.Abbrev })));
const stockExchangeModel = computed({
  get: () => form.value.stockExchange ?? '',
  set: (v) => {
    form.value.stockExchange = v === '' ? null : v;
  },
});

const companiesOptions = computed(() => companies.value.map((c) => ({ label: c.accountName, value: String(c.accountID) })));
const parentAccountModel = computed({
  get: () => (form.value.parentAccountID != null ? String(form.value.parentAccountID) : ''),
  set: (v) => {
    form.value.parentAccountID = v === '' ? null : Number(v);
  },
});

const isPublic = computed(() => form.value.ownershipType === 1);

const companyValidationModalTitle = computed(() => {
  if (companySearchLoading.value) return 'Searching...';
  if (form.value.stockExchange && form.value.stockTicker) {
    return `Results for ${form.value.stockExchange.toUpperCase()}: ${form.value.stockTicker.toUpperCase()}`;
  }
  return 'Results';
});

const isFormDirty = computed(() => JSON.stringify(initialFormValue.value) !== JSON.stringify(form.value));

// ---------
// watchers
// ---------
watch(isPublic, (value) => {
  if (!value) {
    form.value.stockTicker = '';
    form.value.stockExchange = null;
  }
});

watch(
  () => form.value.noParentAccount,
  (noParent) => {
    if (noParent) {
      form.value.parentAccountID = null;
    } else {
      form.value.parentAccountID = account.value.parentAccountID || null;
      if (form.value.parentAccountID === Number(account.value.accountID)) {
        form.value.parentAccountID = null;
      }
    }
  },
);

watch(
  () => form.value.stockExchange,
  (stockExchange) => {
    form.value.capIQId = stockExchange !== account.value.stockExchange ? null : account.value.capIQId;
  },
);

watch(
  () => form.value.stockTicker,
  (stockTicker) => {
    form.value.capIQId = stockTicker !== account.value.stockTicker ? null : account.value.capIQId;
  },
);

// Keep the wizard's shared demographics snapshot in sync so the stepper's
// issue indicators react to edits made here.
watchEffect(() => {
  if (loading.value) return;
  wizard.demographicsInfo.value = {
    domainName: form.value.domainName || null,
    noDomain: form.value.domainDoesNotExist,
  };
});

// Gate the wizard's Next button + surface the inline error in the footer.
watchEffect(() => {
  if (domainInvalid.value) {
    wizard.setCanAdvance(false);
    wizard.setFooterHint({ text: 'Fix the domain name format to continue', color: 'red' });
  } else {
    wizard.setCanAdvance(true);
    wizard.setFooterHint({ text: 'Changes save automatically when you move between steps', color: 'muted' });
  }
});

// -----------
// lifecycle
// -----------
onMounted(async () => {
  try {
    const accountId = wizard.accountId.value;
    const [sicCodes, stockExchangesResult, companiesResult, accountResult, attributes, attributeTypes, industries] = await Promise.all([
      referenceService.sicCodes(),
      referenceService.stockExchanges(),
      accountService.list(),
      accountService.findById(accountId),
      accountService.attributes(String(accountId)),
      referenceService.accountAttributeTypes(),
      referenceService.industries(),
    ]);

    allSics.value = sicCodes;
    allIndustries.value = industries;
    stockExchanges.value = stockExchangesResult;
    allCompanies.value = companiesResult;

    // only show attributes that match the account's ownership type; if unknown (null) display all
    accountAttributes.value = attributeTypes
      .filter((t) => (accountResult.ownershipType ? accountResult.ownershipType === t.ownershipType || t.ownershipType === 'Both' : true))
      .map((t) => ({ title: t.name, value: t.id }));
    form.value.accountAttributes = accountAttributes.value.filter((t) => attributes.find((a) => a === Number(t.value))).map((t) => Number(t.value));

    account.value = accountResult;

    form.value.displayName = accountResult.displayName;
    const sicCode = sicCodes.find((i) => i.sicCode === accountResult.sIC);
    if (sicCode) form.value.sic = sicCode.sicCode;
    form.value.industry = industries.find((i) => i.ID === accountResult.industryCode)?.ID ?? null;
    form.value.stockTicker = accountResult.stockTicker;
    form.value.stockExchange = stockExchangesResult.find((e) => e.Abbrev.trim() === accountResult.stockExchange?.trim())?.Abbrev ?? null;
    form.value.ownershipType = { Private: 0, Public: 1, null: 2 }[accountResult.ownershipType] ?? null;
    form.value.noParentAccount = accountResult.noParent;
    form.value.parentAccountID =
      companiesResult.find((c) => c.accountID === accountResult.parentAccountID && c.accountID !== Number(accountResult.accountID))?.accountID ?? null;
    form.value.domainName = accountResult.domainName;
    form.value.domainDoesNotExist = accountResult.noDomain;
    form.value.capIQId = accountResult.capIQId;

    initialFormValue.value = { ...form.value };
  } catch (err) {
    console.error(err);
    toast.error('Failed to load client demographics.');
  } finally {
    loading.value = false;
  }
});

// Persist on wizard navigation (Next / Back / stepper click).
const unregisterNavGuard = wizard.registerNavGuard(saveDemographics);

onBeforeUnmount(() => {
  unregisterNavGuard();
  wizard.setCanAdvance(true);
  wizard.setFooterHint(null);
});

// ----------
// functions
// ----------
function toggleAccountAttribute(id: number) {
  const idx = form.value.accountAttributes.indexOf(id);
  if (idx >= 0) {
    form.value.accountAttributes = form.value.accountAttributes.filter((x) => x !== id);
  } else {
    form.value.accountAttributes = [...form.value.accountAttributes, id];
  }
}

async function saveDemographics() {
  // Nothing to do until the account has loaded or when no edits were made.
  if (loading.value || !initialFormValue.value || !isFormDirty.value) return;

  const request = {
    stockTicker: form.value.stockTicker,
    stockExchange: form.value.stockExchange,
    ownershipType: ['Private', 'Public', null][form.value.ownershipType ?? 2], // 2 is unknown
    sIC: form.value.sic === 'null' ? null : form.value.sic,
    noParent: form.value.noParentAccount,
    parentAccountID: form.value.parentAccountID,
    domainName: form.value.domainName,
    noDomain: form.value.domainDoesNotExist,
    attributes: form.value.accountAttributes,
    industrycode: !form.value.industry ? null : form.value.industry,
    displayName: form.value.displayName,
    // Account Status lives only on the standalone edit screen; keep the account active here.
    active: !account.value.inactiveDate,
    inactiveReason: account.value.inactiveReason || null,
    capIQId: form.value.capIQId,
  };

  // clear out the capIQId if the ownership type is set to non-public
  if (request.ownershipType !== 'Public') {
    request.capIQId = null;
  }

  try {
    const res = await accountService.update(wizard.accountId.value, request);
    initialFormValue.value = { ...form.value };
    if (res.epicFail) {
      toast.warning('Client demographics saved, however there was an error writing back to Epic.');
    }
  } catch (err) {
    console.error(err);
    const message = (err as AxiosError)?.isAxiosError ? 'ERROR: ' + (err as AxiosError).response?.data : FailedToSaveAccount;
    toast.error(message);
  }
}

async function validateCompany() {
  if (form.value.stockTicker && form.value.stockExchange) {
    showCompanyPickerModal.value = true;
    companySearchLoading.value = true;
    companySearchResults.value = await accountService.searchForPublicCompany(form.value.stockTicker, form.value.stockExchange);
    companySearchLoading.value = false;
  }
}

function cancelCompanyValidationModal() {
  showCompanyPickerModal.value = false;
  companySearchSelection.value = undefined;
}

function confirmCompanyValidationModal() {
  if (companySearchSelection.value) {
    form.value.capIQId = companySearchSelection.value;
    showCompanyPickerModal.value = false;
  }
}
</script>

<template>
  <div class="p-6">
    <h2 class="mb-1 text-base font-semibold">Client demographics</h2>
    <p class="mb-4 text-sm text-base-content/60">
      Review and correct the client's account metadata. Changes are saved automatically when you move between steps.
    </p>

    <div
      v-if="loading"
      class="flex justify-center py-12">
      <Spinner
        size="lg"
        type="spinner"
        class="loading-primary" />
    </div>

    <form
      v-else
      @submit.prevent>
      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div>
          <h2>Display Name</h2>
          <p>
            Enter a Display Name for this client. This name will replace the Epic account name in certain visualizations for conciseness and clarity.
          </p>
        </div>
        <div>
          <TextField
            v-model="form.displayName"
            :error="displayNameErrors.length > 0"
            :error-message="displayNameErrors[0]"
            data-test="displayName"
            @update:model-value="v$.form.displayName.$touch()"
            @blur="v$.form.displayName.$touch()" />
        </div>
      </fieldset>

      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div>
          <h2>SIC Industry</h2>
          <p>
            Assign an industry to the client based off of SIC Codes. These industries are used for client benchmarking as well as book of business
            visualizations.
          </p>
        </div>
        <SICCodesAutocompleteInput
          v-model="form.sic"
          class="w-full"
          input-class="input-md border-base-300" />
      </fieldset>

      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div>
          <h2>{{ orgName() }} Industry</h2>
          <p>
            The {{ orgName() }} industry is a company-defined classification of accounts and is assigned in Epic in the "Account Source" field. If
            the Account Source field is updated in Epic, the change will be reflected on the Platform the next day.
          </p>
        </div>
        <div>
          <Combobox
            v-model="industryModel"
            :options="industryOptions"
            placeholder="Industry"
            data-test="industry" />

          <div
            v-if="!account.sourceAccountId"
            class="flex items-center gap-1">
            <TriangleAlert class="text-warning" />
            <span class="text-base-content/70"> Writing back to Epic is not available for this client. </span>
          </div>
        </div>
      </fieldset>

      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div>
          <h2>Ownership Type</h2>
          <p>
            If a client is publicly traded, select "Public," enter the stock ticker symbol, and select the stock exchange on which the company is
            listed.
          </p>
          <p class="mt-2">If a client is privately held, ensure "Private" is selected.</p>
        </div>
        <div>
          <div class="mb-8 join">
            <button
              @click.prevent="form.ownershipType = 0"
              class="btn join-item btn-sm"
              :class="{ 'btn-primary': form.ownershipType === 0 }">
              Private
            </button>
            <button
              @click.prevent="form.ownershipType = 1"
              class="btn join-item btn-sm"
              :class="{ 'btn-primary': form.ownershipType === 1 }">
              Public
            </button>
            <button
              @click.prevent="form.ownershipType = 2"
              class="btn join-item btn-sm"
              :class="{ 'btn-primary': form.ownershipType === 2 }">
              Unknown
            </button>
          </div>

          <div
            class="grid grid-cols-[0.4fr_0.4fr_0.2fr] gap-4"
            v-if="isPublic">
            <TextField
              v-model="form.stockTicker"
              label="Stock Ticker"
              placeholder="Ticker Symbol"
              :error="stockTickerErrors.length > 0"
              :error-message="stockTickerErrors[0]"
              data-test="stockTicker"
              @update:model-value="v$.form.stockTicker.$touch()"
              @blur="v$.form.stockTicker.$touch()" />
            <div>
              <label class="label">
                <span class="label-text">Stock Exchange</span>
              </label>
              <Combobox
                v-model="stockExchangeModel"
                :options="stockExchangeOptions"
                placeholder="Stock Exchange"
                data-test="stockExchange" />
            </div>
            <div class="mt-3 flex h-fit items-center gap-3">
              <CircleCheck
                :class="{
                  'text-base-content/30': !form.capIQId || !form.stockTicker || !form.stockExchange,
                  'text-success': form.capIQId && form.stockTicker && form.stockExchange,
                }" />
              <button
                @click.prevent="validateCompany"
                class="btn join-item btn-sm btn-primary"
                :class="{ 'btn-disabled': !form.stockTicker || !form.stockExchange }">
                Validate
              </button>
            </div>
          </div>
        </div>
      </fieldset>

      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div>
          <h2>Attributes</h2>
          <p>
            Select one or more attributes from the provided list to categorize this account. If none apply, simply leave the check boxes deselected.
            These selections will facilitate search capabilities on the Platform, such as the Client Management and Policy Explorer pages. Selections
            can be removed as needed.
          </p>
        </div>
        <div>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="attr in accountAttributes"
              :key="attr.value"
              type="button"
              :data-test="`attr-${attr.value}`"
              :aria-pressed="form.accountAttributes.includes(Number(attr.value))"
              @click="toggleAccountAttribute(Number(attr.value))"
              class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
              :class="
                form.accountAttributes.includes(Number(attr.value))
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-base-300 text-base-content/70 hover:border-base-content/40 hover:bg-base-200'
              ">
              <Check
                v-if="form.accountAttributes.includes(Number(attr.value))"
                :size="14" />
              {{ attr.title }}
            </button>
          </div>
        </div>
      </fieldset>

      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div>
          <h2>Parent Company</h2>
          <p>
            In certain circumstances, clients may be a subsidiary of a parent company that also has risk placed with CAC. Where applicable, companies
            can be grouped under their parent company in order to provide a portfolio view of all policies, claims, etc.
          </p>
        </div>
        <div>
          <Combobox
            v-model="parentAccountModel"
            :options="companiesOptions"
            placeholder="Parent Company"
            :disabled="form.noParentAccount" />

          <label
            v-if="isAdmin"
            for="no-parent"
            class="label mt-2 flex items-center justify-start gap-2">
            <input
              type="checkbox"
              id="no-parent"
              class="checkbox border border-solid border-base-300 checkbox-sm"
              v-model="form.noParentAccount"
              :checked="form.noParentAccount" />
            <span>Client Does Not Have Parent</span>
          </label>

          <Alert
            v-else-if="form.noParentAccount"
            type="info"
            :icon="Info"
            class="mt-4 text-xs">
            Selecting a parent account for this client has been disabled. To adjust this setting, please contact
            <a href="mailto:platform@cacspecialty.com">platform@cacspecialty.com</a>.
          </Alert>
        </div>
      </fieldset>

      <fieldset
        class="grid grid-cols-1 gap-x-16 gap-y-3 border-0 border-b border-base-300 py-6 md:grid-cols-[2fr_3fr] [&>div:first-child>h2]:text-base [&>div:first-child>h2]:font-semibold [&>div:first-child>p]:mt-1 [&>div:first-child>p]:text-sm [&>div:first-child>p]:text-base-content/60">
        <div><h2>Domain Name</h2></div>
        <div>
          <TextField
            v-model="form.domainName"
            placeholder="example.com"
            hint="e.g. example.com"
            :error="domainNameErrors.length > 0"
            :error-message="domainNameErrors[0]"
            data-test="domain"
            :disabled="form.domainDoesNotExist"
            @update:model-value="v$.form.domainName.$touch()"
            @blur="v$.form.domainName.$touch()" />

          <label
            for="no-domain"
            class="label flex items-center justify-start gap-2">
            <input
              type="checkbox"
              id="no-domain"
              class="checkbox border border-solid border-base-300 checkbox-sm"
              v-model="form.domainDoesNotExist"
              :checked="form.domainDoesNotExist" />
            <span>Domain Does Not Exist</span>
          </label>
        </div>
      </fieldset>
    </form>

    <AppModal
      :title="companyValidationModalTitle"
      size="lg"
      actionButtonLabel="Confirm"
      :action-disabled="companySearchSelection === undefined"
      :show="showCompanyPickerModal"
      :handle-cancel="cancelCompanyValidationModal"
      :handle-action="confirmCompanyValidationModal"
      modal-class="overflow-y-auto">
      <template v-slot:content>
        <div
          v-if="companySearchLoading"
          class="loading mx-auto flex loading-lg justify-center loading-dots" />

        <template v-if="!companySearchLoading">
          <div
            v-if="companySearchResults.length > 0"
            class="max-h-screen overflow-y-auto">
            <p class="mb-4">The following companies were found:</p>

            <div
              v-for="company in companySearchResults"
              :key="company.companyId">
              <div class="mb-4 flex items-center gap-2">
                <input
                  type="radio"
                  name="company"
                  class="radio border border-solid radio-sm radio-primary"
                  :value="company.companyId"
                  @click="companySearchSelection = company.companyId" />
                <div>
                  <p class="text-lg font-bold">{{ company.name }}</p>
                  <div class="flex items-center gap-1">
                    <span class="text-sm font-bold">{{ company.exchangeSymbol }}:</span>
                    <span class="text-sm font-bold">{{ company.tickerSymbol }}</span>
                    <span
                      class="ml-2 badge text-xs font-bold uppercase"
                      :class="{
                        'badge-soft badge-success': company.tickerStatus.toLowerCase() === 'active',
                        'badge-ghost': company.tickerStatus !== 'Active',
                      }">
                      {{ company.tickerStatus }}
                    </span>
                  </div>
                  <div class="text-sm text-base-content/60">{{ company.address }}</div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>No results found.</div>
        </template>
      </template>
    </AppModal>
  </div>
</template>
